import LoggerService,{formatTrace,formatInfo} from '../../LoggerService';
import {
  _checkTokens,
  _checkErrors,
  _tryOrRefresh,
  I_COMMON_GET_ALL_PARAMS,
  I_COMMON_GET_ITEM_PARAMS,
  I_COMMON_DELETE_PARAMS,
  I_COMMON_IMPORT_PARAMS,
  I_COMMON_CREATE_PARAMS,
  I_COMMON_PUT_PARAMS,
} from './_base';

import I_COLLABORATORS,{I_COLLABORATOR,I_IMPORT_COLLABORATORS_OK} from '../interfaces/I_COLLABORATORS';
import {
  getCollaboratorsResponse,
  postCollaboratorResponse,
  deleteCollaboratorResponse,
  getCollaboratorResponse,
  putCollaboratorResponse,
  importCollaboratorsResponse,
} from '../ApiEndpoints';

// ---------------------------------
// --- COLLABORATEURS
// ---------------------------------

const logger = LoggerService.getInstance();

interface I_GET_PARAMS extends I_COMMON_GET_ALL_PARAMS {
  functions: string;
  directions: string;
  hubs: string;
  sites: string;
  skills: string;
}
interface I_COLLABORATOR_FIELDS {
  /** email du collaborateur */
  email: string;
  /** nom du collaborateur */
  lastname: string;
  /** prénom du collaborateur */
  firstname: string;
  /** Matricule du collaborateur */
  id_osac: string;
  /** Site OSAC du collaborateur */
  office_site: string;
  /** Direction du collaborateur */
  direction: string;
  /** Pôle de direction du collaborateur (optionnel, les directeurs n'en ont pas) */
  hub?: string;
  /** Fonction/Poste du collaborateur */
  func: string; // NB : function est un mot clé
  /** Volume horaire du collaborateur */
  hourly_volume: number;
  /** true si le collaborateur ne peut pas être attribué à une activité */
  non_attribuable: boolean;
  /** Compétences du collaborateur */
  skills: number[],
}
interface I_CREATE_COLLABORATOR_PARAMS extends I_COMMON_CREATE_PARAMS, I_COLLABORATOR_FIELDS {}
interface I_PUT_COLLABORATOR_PARAMS extends I_COMMON_PUT_PARAMS, I_COLLABORATOR_FIELDS {}

// ----

const isMissingField = (item:I_COLLABORATOR_FIELDS) => {
  // tous les champs sont obligatoire sauf hub qui peut etre vide pour les directeurs
  const emailKO = !item.email || item.email === null || item.email.trim() === '';
  const lastnameKO = !item.lastname || item.lastname === null || item.lastname.trim() === '';
  const firstnameKO = !item.firstname || item.firstname === null || item.firstname.trim() === '' ;
  const idosacKO = !item.id_osac || item.id_osac.trim() === '';
  const officesiteKO = !item.office_site || item.office_site.trim() === '';
  const functionKO = !item.func || item.func.trim() === '';
  const directionKO = !item.direction || item.direction.trim() === '';
  const hubKO = false;  // TODO ? vérifier selon direction = director
  const hourlyVolumeKO = item.hourly_volume === undefined;
  const skillsKO = !item.skills || item.skills.length === 0;
  return emailKO || lastnameKO || firstnameKO || idosacKO || officesiteKO ||
          functionKO || directionKO || hubKO || hourlyVolumeKO || skillsKO;
}

// ----



/**
 * Récupère la liste des compétences
 * functions,directions,hubs,sites,skills : passer les valeurs sous forme 'xxx,yyy,zzz'
 * @param obj
 * @returns
 */
const getList = async ({accessToken,refreshToken,page,functions,directions,hubs,sites,skills,dispatch,state}:I_GET_PARAMS):Promise<I_COLLABORATORS> => {
  logger.debug('ApiServices - getCollaborators - ...');
  // pre-verification qu'on a bien des token, sinon sert a rien de faire la requete
  await _checkTokens(accessToken,refreshToken);
  // requete le endpoint
  const response = await _tryOrRefresh({
    request: (usedAccessToken:string) => {
      return getCollaboratorsResponse({accessToken:usedAccessToken,page,functions,directions,hubs,sites,skills});
    },
    accessToken: accessToken || '', // normalement c'est non vide car on a fait un checkTokens
    refreshToken: refreshToken || '', // normalement c'est non vide car on a fait un checkTokens
    dispatch,state
  });
  logger.trace(...formatTrace('ApiServices - getCollaborators - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:I_COLLABORATORS = await response.json();
  logger.debug('ApiServices - getCollaborators - ok, returned body',body);
  return body;
};


/**
 * Crée un collaborateur
 * @param param0
 * @returns
 */
const create = async (args:I_CREATE_COLLABORATOR_PARAMS):Promise<string> => {
  logger.debug('ApiServices - createCollaborator - ...');
  const {
    accessToken,refreshToken,
    dispatch,state,
    email,firstname,lastname,
    id_osac, office_site, direction, hub, func, hourly_volume, non_attribuable, skills,
  } = args;
  // pre-verification qu'on a bien des token, sinon sert a rien de faire la requete
  await _checkTokens(accessToken,refreshToken);
  // pre-verification qu'on a bien des valeurs obligatoires, sinon sert a rien de faire la requete
  if (isMissingField(args)) {
    const error = {
      error: true,
      errorCode: 1000,
      errorText: 'Missing field(s)',
      errorMessage: 'Missing field(s) to post new collaborator',
    };
    return Promise.reject(error);
  }
  // requete le endpoint
  const response = await _tryOrRefresh({
    request: (usedAccessToken:string) => {
      return postCollaboratorResponse({
        accessToken:usedAccessToken,
        email,firstname,lastname,
        id_osac, office_site, direction, hub, func, hourly_volume, non_attribuable, skills,
      });
    },
    accessToken,refreshToken,
    dispatch,state,
  });
  logger.trace(...formatTrace('ApiServices - createCollaborator - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:string = await response.text();
  logger.debug('ApiServices - createCollaborator - ok, returned body',body);
  logger.info(...formatInfo('Collaborator created!'));
  return body;
};

/**
 * Supprime un collaborateur
 * @param param0
 * @returns
 */
const remove = async ({accessToken,refreshToken,id,dispatch,state}:I_COMMON_DELETE_PARAMS):Promise<string> => {
  logger.debug('ApiServices - deleteCollaborator - ...');
  // pre-verification qu'on a bien des token, sinon sert a rien de faire la requete
  await _checkTokens(accessToken,refreshToken);
  // requete le endpoint
  const response = await _tryOrRefresh({
    request: (usedAccessToken:string) => {
      return deleteCollaboratorResponse({accessToken:usedAccessToken,id});
    },
    accessToken: accessToken || '', // normalement c'est non vide car on a fait un checkTokens
    refreshToken: refreshToken || '', // normalement c'est non vide car on a fait un checkTokens
    dispatch,state
  });
  logger.trace(...formatTrace('ApiServices - deleteCollaborator - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:string = await response.text();
  logger.debug('ApiServices - deleteCollaborator - ok, returned body',body);
  logger.info(...formatInfo('Collaborator deleted!'));
  return body;
};


/**
 * Modifie un collaborateur
 * @param param0
 * @returns
 */
const update = async (args:I_PUT_COLLABORATOR_PARAMS):Promise<string> => {
  logger.debug('ApiServices - updateCollaborator - ...');
  const {
    accessToken,refreshToken,
    dispatch,state,
    id,
    email,firstname,lastname,
    id_osac, office_site, direction, hub, func, hourly_volume, non_attribuable, skills,
  } = args;
  // pre-verification qu'on a bien des token, sinon sert a rien de faire la requete
  await _checkTokens(accessToken,refreshToken);
  // pre-verification qu'on a bien des valeurs obligatoires, sinon sert a rien de faire la requete
  if (isMissingField(args)) {
    const error = {
      error: true,
      errorCode: 1000,
      errorText: 'Missing field(s)',
      errorMessage: 'Missing field(s) to update collaborator',
    };
    return Promise.reject(error);
  }
  // requete le endpoint
  const response = await _tryOrRefresh({
    request: (usedAccessToken:string) => {
      return putCollaboratorResponse({
        accessToken:usedAccessToken,id,
        email,firstname,lastname,
        id_osac, office_site, direction, hub, func, hourly_volume, non_attribuable, skills,
      });
    },
    accessToken: accessToken || '', // normalement c'est non vide car on a fait un checkTokens
    refreshToken: refreshToken || '', // normalement c'est non vide car on a fait un checkTokens
    dispatch,state
  });
  logger.trace(...formatTrace('ApiServices - updateCollaborator - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:string = await response.text();
  logger.debug('ApiServices - updateCollaborator - ok, returned body',body);
  logger.info(...formatInfo('Collaborator updated!'));
  return body;
};

/**
 * Récupère le collaborateur
 * @param param0
 * @returns
 */
const getItem = async ({accessToken,refreshToken,id,dispatch,state}:I_COMMON_GET_ITEM_PARAMS) => {
  logger.debug('ApiServices - getCollaborator - ...');
  // pre-verification qu'on a bien des token, sinon sert a rien de faire la requete
  await _checkTokens(accessToken,refreshToken);
  // requete le endpoint
  const response = await _tryOrRefresh({
    request: (usedAccessToken:string) => {
      return getCollaboratorResponse({accessToken:usedAccessToken,id});
    },
    accessToken: accessToken || '', // normalement c'est non vide car on a fait un checkTokens
    refreshToken: refreshToken || '', // normalement c'est non vide car on a fait un checkTokens
    dispatch,state
  });
  logger.trace(...formatTrace('ApiServices - getCollaborator - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:I_COLLABORATOR = await response.json();
  logger.debug('ApiServices - getCollaborator - ok, returned body',body);
  return body;
};


/**
 * Modifie un type de tache
 * @param param0
 * @returns
 */
const importList = async ({
  accessToken,refreshToken,file, dispatch,state
}:I_COMMON_IMPORT_PARAMS):Promise<I_IMPORT_COLLABORATORS_OK> => {
  logger.debug('ApiServices - importCollaborators - ...');
  // pre-verification qu'on a bien des token, sinon sert a rien de faire la requete
  await _checkTokens(accessToken,refreshToken);
  // pre-verification qu'on a bien des valeurs obligatoires, sinon sert a rien de faire la requete
  if (!file) {
    const error = {
      error: true,
      errorCode: 1000,
      errorText: 'Missing file',
      errorMessage: 'Missing file to post collaborator',
    };
    return Promise.reject(error);
  }
  // requete le endpoint
  const response = await _tryOrRefresh({
    request: (usedAccessToken:string) => {
      return importCollaboratorsResponse({
        accessToken:usedAccessToken,file,
      });
    },
    accessToken: accessToken || '', // normalement c'est non vide car on a fait un checkTokens
    refreshToken: refreshToken || '', // normalement c'est non vide car on a fait un checkTokens
    dispatch,state
  });
  logger.trace(...formatTrace('ApiServices - importCollaborators - returned response',response));
  // verifie que la reponse est ok, sinon throw error
  await _checkErrors(response);
  // extrait les données
  const body:I_IMPORT_COLLABORATORS_OK = await response.json();
  logger.debug('ApiServices - importCollaborators - ok, returned body',body);
  logger.info(...formatInfo('Collaborators imported!'));
  return body;
};

const collaboratorsServices = {
  getList,
  create,
  remove,
  update,
  getItem,
  importList,
};

export default collaboratorsServices;
